import { useState } from 'react';
import { Button, Modal, Table } from '@react-apps/design-system';
import type { ColumnDef } from '@react-apps/design-system';
import type { PerkPreset } from '../../domain/entities/catalog.entity';
import {
  usePerkPresetMutations,
  usePerkPresets,
} from '../hooks/use-perk-presets';
import { CreatePerkPresetFromSourceModal } from './CreatePerkPresetFromSourceModal';
import { CreatePerkPresetModal } from './CreatePerkPresetModal';
import { EditPerkPresetModal } from './EditPerkPresetModal';
import { PerkPresetItemsEditorModal } from './PerkPresetItemsEditorModal';
import { Pagination } from '@react-apps/design-system';
import { RefreshButton } from '@/shared/RefreshButton';
import { usePageQueryParam } from '@/shared/hooks/use-page-query-param';

const PER_PAGE = 10;

/**
 * Presets são atalho de configuração: "esta pessoa entrou no time X" ou "este
 * produto é o bundle Y" em um clique — na tela de perks do usuário e na de
 * perks do produto. Não são concedíveis nem aparecem em `@Roles(...)`: quem
 * manda continua sendo cada perk clonado.
 */
export function PerkPresetsPanel() {
  const [page, setPage] = usePageQueryParam();
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [itemsTarget, setItemsTarget] = useState<PerkPreset | null>(null);
  const [editTarget, setEditTarget] = useState<PerkPreset | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<PerkPreset | null>(null);
  const [sourcePreset, setSourcePreset] = useState<PerkPreset | null>(null);
  const { data, isLoading, refetch, isFetching } = usePerkPresets(
    page,
    PER_PAGE,
  );
  const { remove } = usePerkPresetMutations();

  const closeDelete = () => {
    remove.reset();
    setDeleteTarget(null);
  };

  const columns: ColumnDef<PerkPreset>[] = [
    { key: 'name', header: 'Nome' },
    {
      key: 'key',
      header: 'Chave',
      render: (row) => (
        <code className="text-(--color-text-secondary) text-xs">{row.key}</code>
      ),
    },
    {
      key: 'description',
      header: 'Descrição',
      render: (row) => (
        <span className="text-(--color-text-secondary) text-sm">
          {row.description}
        </span>
      ),
    },
    {
      key: 'actions',
      header: '',
      width: '1%',
      render: (row: PerkPreset) => (
        <div className="flex justify-end gap-2">
          <Button
            variant="secondary"
            size="sm"
            className="whitespace-nowrap"
            onClick={() => setItemsTarget(row)}
          >
            Gerenciar perks
          </Button>
          <Button
            variant="ghost"
            size="xs"
            className="whitespace-nowrap"
            onClick={() => setSourcePreset(row)}
          >
            Criar novo baseado neste
          </Button>
          <Button
            variant="ghost"
            size="sm"
            className="whitespace-nowrap"
            onClick={() => setEditTarget(row)}
          >
            Editar
          </Button>
          <Button
            variant="ghost"
            size="sm"
            className="whitespace-nowrap"
            onClick={() => setDeleteTarget(row)}
          >
            Excluir
          </Button>
        </div>
      ),
    },
  ];

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between">
        <p className="text-(--color-text-secondary) text-sm">
          Pacotes nomeados de perks já configurados, aplicáveis de uma vez na
          tela de perks de um usuário ou de um produto.
        </p>
        <div className="flex shrink-0 items-center gap-2">
          <RefreshButton
            onRefresh={() => void refetch()}
            isRefreshing={isFetching}
          />
          <Button onClick={() => setIsCreateOpen(true)}>Criar preset</Button>
        </div>
      </div>

      <CreatePerkPresetModal
        open={isCreateOpen}
        onClose={() => setIsCreateOpen(false)}
      />

      <Table
        columns={columns}
        data={data?.items ?? []}
        keyExtractor={(row) => row.id}
        loading={isLoading}
        emptyMessage="Nenhum preset encontrado."
        striped
      />

      <Pagination
        page={page}
        total={data?.total ?? 0}
        perPage={PER_PAGE}
        loading={isLoading}
        label="preset"
        onPageChange={setPage}
      />

      {itemsTarget && (
        <PerkPresetItemsEditorModal
          preset={itemsTarget}
          open
          onClose={() => setItemsTarget(null)}
        />
      )}

      <CreatePerkPresetFromSourceModal
        source={sourcePreset}
        onClose={() => setSourcePreset(null)}
      />

      {editTarget && (
        <EditPerkPresetModal
          preset={editTarget}
          open
          onClose={() => setEditTarget(null)}
        />
      )}

      {deleteTarget && (
        <Modal
          open
          onClose={closeDelete}
          title={`Excluir "${deleteTarget.name}"?`}
          description="O preset e seus itens somem. Nada do que já foi concedido a partir dele muda — o preset é copiado no uso, não consultado depois."
        >
          <div className="space-y-4">
            {remove.error != null && (
              <p className="text-sm text-red-600">
                {remove.error instanceof Error
                  ? remove.error.message
                  : 'Falha ao excluir preset'}
              </p>
            )}
            <div className="flex justify-end gap-2">
              <Button type="button" variant="ghost" onClick={closeDelete}>
                Cancelar
              </Button>
              <Button
                type="button"
                variant="danger"
                disabled={remove.isPending}
                onClick={() =>
                  remove.mutate(deleteTarget.id, { onSuccess: closeDelete })
                }
              >
                {remove.isPending ? 'Excluindo…' : 'Excluir'}
              </Button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}
